import {
  StateDefinition,
  StateIndex,
} from '@iwentsourcing/core/storage/state-declaration.js';
import { SnapshotIndexClient } from '@iwentsourcing/core/storage/snapshot-index-client.js';
import { iterateResponse } from './iterate-response.js';
import { RemoteDescription } from './remote-description.js';
import { RpcClient } from '../api/create-rpc-client.js';
import { SessionManager } from '../auth/jwt-session.js';
import { Lazy } from '@iwentsourcing/core/lazy/lazy.js';
import { StateValue } from '@iwentsourcing/core/storage/state-value.js';
import { DataSet } from '@iwentsourcing/core/storage/data-set.js';
import { RangeOptions } from '@iwentsourcing/core/storage/range-options.js';
import { FilterStateIndex } from '@iwentsourcing/core/storage/filter-state-index.js';
import { FilterValue } from '@iwentsourcing/core/storage/filter-value.js';
import { FilterKey } from '@iwentsourcing/core/storage/filter-key.js';
import { Telemetry } from '@iwentsourcing/core/tracer/telemetry.js';

export function createRemoteSnapshotIndexClient<
  TState extends StateDefinition<any, any, any>,
  TIndex extends StateIndex<any, any>,
>(
  state: TState,
  index: TIndex,
  keyIndex: number,
  rpc: RpcClient,
  session: SessionManager,
  lazyVersion: Lazy<string>,
  type: 'stream' | 'aggregation',
  streamIdentifier: RemoteDescription,
  stateName: string,
  indexName: string,
  filter: Record<string, StateValue>,
  telemetry: Telemetry,
): SnapshotIndexClient<TState, TIndex> {
  return {
    async *range(
      bookmark: FilterValue<TState, TIndex> | null,
      options: RangeOptions,
    ): AsyncGenerator<DataSet<any>> {
      yield* iterateResponse(
        state,
        bookmark ?? null,
        options,
        async (bookmark, rangeOptions) =>
          rpc['state.index.browse'].call(
            {
              tenant: streamIdentifier.tenant,
              realm: streamIdentifier.realm,
              version: await lazyVersion.resolve(),
              pattern: streamIdentifier.pattern,
              state: stateName,
              index: indexName,
              type,
              bookmark: bookmark ?? undefined,
              open: rangeOptions.open,
              direction: rangeOptions.dir,
              filter,
              args: streamIdentifier.args,
              token: await session.resolveToken(),
              take: undefined,
            },
            options.signal,
          ),
        telemetry,
      );
    },
    filter(
      key: FilterKey<TIndex> & string,
      value: FilterValue<TState, TIndex>,
    ): SnapshotIndexClient<TState, FilterStateIndex<TIndex>> {
      return createRemoteSnapshotIndexClient(
        state,
        index,
        keyIndex + 1,
        rpc,
        session,
        lazyVersion,
        type,
        streamIdentifier,
        stateName,
        indexName,
        { ...filter, [key]: value },
        telemetry,
      );
    },
  };
}
